import {
  useFetchChange,
  useFetchTimeSeries,
  useListDropdownChanges,
} from 'api/anomalies/anomalyServices';
import { type FC, useCallback, useEffect, useRef } from 'react';
import { AbsoluteImpact } from './components/AbsoluteImpact';
import { Activity } from './components/Activity';
import { AnomalyOverview } from './components/AnomalyOverview';
import { DetailsHeader } from './components/DetailsHeader';
import { ImpactContainer } from './components/ImpactContainer';
import { LessonsLearned } from './components/LessonsLearned';
import { RootCause } from './components/RootCause';
import { Status } from './components/Status';
import { downloadPDF } from 'utils/pdf/pdfUtils';
import { useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { BackButton } from '@components/backButton';
import { useNotFoundPage } from 'hooks/useNotFoundPage';

const CHANGES_LIST_PATH = '/app/operations/anomalies';

const AnomalyDetailsPage: FC = () => {
  const pdfRef = useRef<HTMLDivElement | null>(null);
  const { id } = useParams();

  const { data, isLoading, isError, error } = useFetchChange(id);

  const { data: timeSeriesData } = useFetchTimeSeries({
    id: data?.anomaly.connection_point_id_client,
    momentOfDetectedAnomaly: data?.anomaly.moment_change ?? '',
  });

  const { validateAndRedirectToNotFound } = useNotFoundPage();

  const { data: dropdownData } = useListDropdownChanges({
    connectionPointIdClient: data?.anomaly.connection_point_id_client,
  });

  useEffect(() => {
    validateAndRedirectToNotFound(isError, error);
  }, [isError, error]);

  const findPreviousAndNextChange = useCallback(
    (
      inputDate: string,
      datesArray: string[]
    ): { previous: string | null; next: string | null } => {
      const dates: { previous: string | null; next: string | null } = {
        previous: null,
        next: null,
      };

      const inputDateTime = new Date(inputDate).getTime();
      datesArray.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

      for (const dateStr of datesArray) {
        const dateDateTime = new Date(dateStr).getTime();
        if (dateDateTime < inputDateTime) {
          dates.previous = dateStr;
        } else if (dateDateTime > inputDateTime && dates.next === null) {
          dates.next = dateStr;
        }
      }

      if (
        dates.previous === null &&
        timeSeriesData &&
        timeSeriesData?.time_series.length > 0
      ) {
        dates.previous = timeSeriesData?.time_series[0].time;
      }

      return dates;
    },
    [dropdownData, timeSeriesData]
  );

  const previousAndNextDate = useMemo(() => {
    if (!data) return;
    const datesArray =
      dropdownData?.anomalies_detected?.map(
        (anomaly) => anomaly.moment_change
      ) ?? [];
    return findPreviousAndNextChange(data?.anomaly.moment_change, datesArray);
  }, [data, dropdownData, timeSeriesData]);

  return (
    <>
      <div className="flex flex-col gap-6" ref={pdfRef}>
        <BackButton navigateTo={CHANGES_LIST_PATH} />
        <DetailsHeader
          meterName={data?.anomaly.connection_point_id}
          utilityType={data?.anomaly.meter_unit.utility_type}
          momentOfDetection={data?.anomaly.start_values_TS}
          building={data?.anomaly.main_building?.name}
          buildingId={data?.anomaly.main_building?.id}
          downloadHandler={async () => {
            await downloadPDF(pdfRef.current);
          }}
          isLoading={isLoading}
        />
        <ImpactContainer data={data?.anomaly} isLoading={isLoading} />
        <AnomalyOverview
          data={timeSeriesData}
          connectionPointId={data?.anomaly.connection_point_id_client}
          unit={data?.anomaly.meter_unit.unit_type}
          isLoading={isLoading}
          dropdownData={dropdownData}
        />
        <AbsoluteImpact
          anomalyData={data?.anomaly}
          isLoading={isLoading}
          previousAndNextChange={previousAndNextDate}
        />
        <div className="flex gap-6">
          <div className="flex w-1/2">
            <RootCause changeId={id} isLoading={isLoading} />
          </div>

          <div className="flex w-1/2 h-min">
            <Status
              currentUser={data?.anomaly.assigned_to_user}
              currentStatus={data?.anomaly.status}
              timeOnStatus={data?.anomaly.time_on_status}
              statusList={data?.available_statuses.statuses}
              changeId={id}
              isLoading={isLoading}
            />
          </div>
        </div>
        <div className="flex gap-6">
          <div className="w-6/12">
            <Activity changeId={data?.anomaly.id} isLoading={isLoading} />
          </div>
          <div className="w-6/12">
            <LessonsLearned changeId={id} isLoading={isLoading} />
          </div>
        </div>
      </div>
    </>
  );
};
export default AnomalyDetailsPage;
